'use client';

import { Children, isValidElement, useId, useMemo } from 'react';
import { cn } from '@/lib/utils/tw-merge';
import type { UiSliderProps } from './UiSlider.types';
import { UiSliderNavigation } from './UiSliderNavigation';
import { useSlider } from './useSlider';

/** Batteries-included horizontal slider: children become snap items with
 *  prev/next controls above the track. For custom control placement (e.g. in a
 *  section header) compose `useSlider` + `UiSliderNavigation` directly. */
export default function UiSlider({
    children,
    className,
    listClassName,
    itemClassName,
    navigationClassName,
    ariaLabel = 'Slider',
}: UiSliderProps) {
    const items = useMemo(() => Children.toArray(children), [children]);
    const listId = useId();
    const { scrollRef, canScrollLeft, canScrollRight, scrollPrev, scrollNext, onKeyDown } = useSlider<HTMLUListElement>(
        items.length,
    );

    return (
        <div className={className} data-ui-slider role="region" aria-roledescription="carousel" aria-label={ariaLabel}>
            <UiSliderNavigation
                className={cn('mb-6 justify-end', navigationClassName)}
                canScrollLeft={canScrollLeft}
                canScrollRight={canScrollRight}
                onPrev={scrollPrev}
                onNext={scrollNext}
            />

            <ul
                ref={scrollRef}
                id={listId}
                tabIndex={0}
                onKeyDown={onKeyDown}
                className={cn(
                    'scrollbar-hidden flex snap-x snap-mandatory overflow-x-auto focus-visible:outline-none',
                    listClassName,
                )}
            >
                {items.map((child, i) => {
                    const key = isValidElement(child) && child.key != null ? String(child.key) : String(i);
                    return (
                        <li key={key} className={cn('shrink-0 snap-start', itemClassName)}>
                            {child}
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}
